import { useCallback, useEffect, useMemo, useState } from "react";
import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";

interface UseConfiguracionNovedadesHorasExtraViewModelArgs {
    orgID: string;
    service: NovedadesExportService;
}

export const useConfiguracionNovedadesHorasExtraViewModel = ({
    orgID,
    service,
}: UseConfiguracionNovedadesHorasExtraViewModelArgs) => {
    const [profiles, setProfiles] = useState<NovedadesExportServiceModel.WorkedHoursCalculationProfile[]>([]);
    const [selectedProfileID, setSelectedProfileID] = useState<string>("");
    const [savedProfileID, setSavedProfileID] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const selectedProfile = useMemo(() => {
        return profiles.find((profile) => profile.profileID === selectedProfileID);
    }, [profiles, selectedProfileID]);

    const hasChanges = selectedProfileID !== savedProfileID;

    const analyticsPayload = useCallback((profileID = selectedProfileID) => ({
        profileID,
        previousProfileID: savedProfileID || undefined,
    }), [savedProfileID, selectedProfileID]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(undefined);
        try {
            const [availableProfiles, configuration] = await Promise.all([
                service.listWorkedHoursCalculationProfiles(orgID),
                service.getWorkedHoursCalculationConfiguration(orgID),
            ]);
            setProfiles(availableProfiles);
            const nextSelectedProfileID = configuration.workedHoursCalculationProfileID ||
                availableProfiles[0]?.profileID ||
                "";
            setSelectedProfileID(nextSelectedProfileID);
            setSavedProfileID(nextSelectedProfileID);
        } catch (loadError) {
            console.error(loadError);
            setError("No se pudo cargar la configuración de horas extra.");
        } finally {
            setLoading(false);
        }
    }, [orgID, service]);

    useEffect(() => {
        void load();
    }, [load]);

    const save = useCallback(async (): Promise<boolean> => {
        if (!selectedProfileID) {
            return false;
        }
        setSaving(true);
        setError(undefined);
        NovedadesExportAnalyticsHelper.trackExtraHoursSaveSubmitted(analyticsPayload());
        try {
            const result = await service.updateWorkedHoursCalculationConfiguration(
                orgID,
                selectedProfileID
            );
            NovedadesExportAnalyticsHelper.trackExtraHoursSaveSucceeded(
                analyticsPayload(result.workedHoursCalculationProfileID),
            );
            setSelectedProfileID(result.workedHoursCalculationProfileID);
            setSavedProfileID(result.workedHoursCalculationProfileID);
            return true;
        } catch (saveError) {
            console.error(saveError);
            NovedadesExportAnalyticsHelper.trackExtraHoursSaveFailed(analyticsPayload());
            setError("No se pudo guardar la configuración de horas extra.");
            return false;
        } finally {
            setSaving(false);
        }
    }, [analyticsPayload, orgID, selectedProfileID, service]);

    const cancel = useCallback(() => {
        NovedadesExportAnalyticsHelper.trackExtraHoursCanceled(analyticsPayload());
        setSelectedProfileID(savedProfileID);
    }, [analyticsPayload, savedProfileID]);

    const selectProfile = useCallback((profileID: string) => {
        setSelectedProfileID(profileID);
        NovedadesExportAnalyticsHelper.trackExtraHoursProfileSelected(profileID);
    }, []);

    return {
        profiles,
        selectedProfile,
        selectedProfileID,
        setSelectedProfileID: selectProfile,
        loading,
        saving,
        error,
        hasChanges,
        load,
        save,
        cancel,
    };
};
